/**
 *
 * LinkMessage
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import calendarIcon from 'assets/icons/calendarIcon.svg';
import PageLoader from 'ui/components/Loaders/PageLoader';
import { slideUp } from 'styles/animation';
import backIcon from 'assets/icons/back.svg';
import { eventTypes } from 'utils/eventTypes';
import { saveEventToLocalStorage } from 'utils/saveToLocalStorage';
import isColorDark from 'utils/isColorDark';
import { connect } from 'react-redux';
import * as globalActions from 'ui/containers/App/actions';
import {WINDOW_POSTMESSAGE_TYPES, TRIGGER_EVENT_TYPES} from "../../containers/App/constants";
import * as globalSelectors from "../../containers/App/selectors";
import {createStructuredSelector} from "reselect";
import ReactHtmlParser from "react-html-parser";
import { postMessageToWindow } from 'utils/index';

const Link = styled.div`
  float: left;
  width: 100%;
  opacity: 0;
  transform: translateY(10px);
  will-change: transform;
  animation: ${slideUp} 0.4s ease-in-out 0.08s forwards;
`;
const LinkBody = styled.div`
  position: relative;
  display: flex;
  height: inherit;
  width: ${p => p.width ? p.width : 'calc(100% - 40px)'};
  color: ${p => p.headerTextColor};
  background: ${p => p.headerBackgroundColor};
  align-items: center;
  border-radius: 15px;
  padding: 10px;
  cursor: ${p => p.isDisabled ? 'no-drop' :'pointer'};
  ${p => p.isDisabled && `opacity: 0.8;`}
  box-sizing: border-box;
`;
const LinkBodyLabel = styled.div`
  font-size: 13px;
  line-height: 20px;
  margin-left: 15px;
`;

const LinkImage = styled.img`
  width: 25px;
  height: 25px;
  position: relative;
`;

const LinkImageWrapper = styled.div`
  width: 50px;
  height: 50px;
  background: ${p => (p.isColorDark ? p.theme.palette.white + '33' : p.theme.palette.black + '33')};
  mix-blend-mode: normal;
  border-radius: 15px;
  padding: 13px;
  box-sizing: border-box;
`;

const LinkTitle = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  margin-bottom: 3px;
  color: ${p => p.headerTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  max-width: 100%;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
`;
const LinkSubtitle = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
  color: ${p => p.headerTextColor};

  mix-blend-mode: normal;
  opacity: 0.6;
`;

export const LinkMessage = ({
  message,
  userSettingColor,
  userConfig,
  currentChannel,
  setLinkIframe,
  isDisabled
}) => {
  const link = message? (message.link ? message.link : {}) : {};
  
  // Check if URL is from Microsoft domain
  const isMicrosoftDomain = (url) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      
      // List of Microsoft domains
      const microsoftDomains = [
        'microsoft.com',
        'outlook.com',
        'outlook.live.com',
        'office.com',
        'office365.com',
        'live.com',
        'microsoftonline.com',
        'teams.microsoft.com',
        'sharepoint.com',
        'onedrive.com',
        'azure.com',
        'windowsazure.com',
        'microsoftazure.com',
        'msn.com',
        'hotmail.com',
        'skype.com'
      ];
      
      // Check if hostname ends with any Microsoft domain
      return microsoftDomains.some(domain => 
        hostname === domain || hostname.endsWith('.' + domain)
      );
    } catch (e) {
      console.error('Invalid URL:', url);
      return false;
    }
  };
  
  const linkClickAction = () => {
    const event = {
      eventType: eventTypes.CALENDAR_LINK_CLICKED,
      timestamp: Date.now(),
      url: userConfig.blogUrl,
      props: {
        channelId: currentChannel,
        link: link.url,
        stepUid: message?.stepUid
      },
    };
    
    postMessageToWindow(
      {
        type: userConfig.eventListenerName,
        event: WINDOW_POSTMESSAGE_TYPES.triggerEvent,
        eventType: TRIGGER_EVENT_TYPES.calendarLinkClicked,
        eventData: {
          channelId: currentChannel,
          link: link.url,
        }
      },
      '*'
    );
    
    saveEventToLocalStorage(event);
    
    // Check if it's a Microsoft domain URL
    if (isMicrosoftDomain(link.url)) {
      // Open Microsoft URLs in new tab
      console.log("inside microsoft domain")
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      // Continue with default behavior for non-Microsoft URLs
      setLinkIframe(link);
    }
  };

  return (
    <div>
      <Link>
        <LinkBody
          onClick={link.url && linkClickAction}
          width={!link.url && '100%'}
          isDisabled={isDisabled}
          headerBackgroundColor={userSettingColor ? userSettingColor.headerBackgroundColor : null}
          headerTextColor={userSettingColor ? userSettingColor.headerTextColor : null}
        >
          <LinkImageWrapper isColorDark={isColorDark(userSettingColor ? userSettingColor.headerBackgroundColor : null)}>
            <LinkImage src={calendarIcon} alt="close" />
          </LinkImageWrapper>
          <LinkBodyLabel>
            <LinkTitle headerTextColor={userSettingColor ? userSettingColor.headerTextColor : null}>
              {ReactHtmlParser(link.title)}
            </LinkTitle>
            <LinkSubtitle
              headerTextColor={userSettingColor ? userSettingColor.headerTextColor : null}
            >
              {link.subtitle}
            </LinkSubtitle>
          </LinkBodyLabel>
        </LinkBody>
      </Link>
    </div>
  );
};

LinkMessage.propTypes = {
  message: PropTypes.objectOf(
    PropTypes.shape({
      link: PropTypes.objectOf(
        PropTypes.shape({
          title: PropTypes.string,
          subtitle: PropTypes.string,
          url: PropTypes.string,
        })
      ),
    })
  ),
};

export const mapDispatchToProps = dispatch => {
  return {
    setLinkIframe: url => dispatch(globalActions.setLinkIframe.start(url)),
  };
};

export const mapStateToProps = (state, props) => {
    return createStructuredSelector({
        userConfig: globalSelectors.makeSelectConfig(),
    });
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(LinkMessage);
